import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { completeTask, fetchMyTasks } from '@/approvals/api'
import type { Task } from '@/approvals/types'
import { BlockState, QueryErrorState } from '@/components/BlockState'
import { Icon, type IconName } from '@/components/Icon'
import { StagePips } from '@/components/StagePips'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatTotals } from '@/lib/format'
import type { Locale } from '@/i18n'
import { TaskProposalsPanel } from '@/ai/TaskProposals'
import { NextStepPanel } from '@/nextstep/NextStepPanel'
import { fetchMyDay } from './api'
import type { AttentionDeal, InvoiceRow, MyDay, NewLead } from './api'
import '@/styles/myday.css'

/**
 * «Мой день» — рабочий стол менеджера (макет MyDay.dc.html).
 *
 * Экран отвечает на вопрос «что мне сделать сейчас», поэтому левая колонка — то, что
 * требует действия (задачи, сделки без шага и застрявшие), правая — контекст дня
 * (встречи, новые лиды, счета моих клиентов). Сводка сверху — ссылки на блоки, а не
 * декоративные плитки: число, по которому нельзя перейти, ничего не помогает сделать.
 *
 * ## Два источника, и каждый блок отвечает за себя
 *
 * Задачи и встречи — `GET /tasks/my` (#79), остальное — `GET /my-day`. Упал один —
 * второй показывается: «счета не загрузились» не повод прятать задачи. Блок без права на
 * источник говорит «нет доступа» словами, пустой — «нет данных» словами: пустота,
 * нарисованная пустотой, читается как «всё сделано».
 *
 * ## Просрочку и застой считает сервер
 *
 * `overdue` у задачи и `stuck` у сделки приходят с сервера: «срок прошёл» — вопрос о
 * текущем времени, а «застряла» — тот же признак, что на доске. Клиент только раскладывает
 * задачи по дням — какой день «сегодня», решает часовой пояс смотрящего.
 */
export function MyDayPage() {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)

  const tasks = useQuery({
    queryKey: ['my-tasks'],
    queryFn: ({ signal }) => fetchMyTasks(signal),
    retry: false,
  })
  const day = useQuery({
    queryKey: ['my-day'],
    queryFn: ({ signal }) => fetchMyDay(signal),
    retry: false,
  })

  const complete = useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      setNotice(null)
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
      // Завершённая задача могла быть единственным шагом сделки.
      void queryClient.invalidateQueries({ queryKey: ['my-day'] })
    },
    onError: (error: unknown) =>
      setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  const now = new Date()
  const noCard = tasks.data?.hasEmployeeCard === false || day.data?.hasEmployeeCard === false

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="page-head">
        <div className="stack" style={{ gap: 0 }}>
          <h1 className="h1">{t('nav.myDay')}</h1>
          <span className="t-sm muted">{longDate(now, locale)}</span>
        </div>
        <span className="spacer" />
        {can(user, 'sales.lead', 'create') && (
          <Link className="btn btn-secondary" to="/leads/new">{t('myday.newLead')}</Link>
        )}
        <Link className="btn btn-ghost" to="/tasks">{t('myday.allTasks')}</Link>
      </div>

      {notice && <div className="alert alert-danger" role="alert">{notice}</div>}

      {noCard ? (
        // Словами, а не пустыми блоками: у учётной записи без карточки сотрудника
        // «моего» нет по устройству, и пустота читалась бы как «всё сделано».
        <div className="state-box">
          <BlockState kind="empty" title={t('myday.noCard.title')} description={t('myday.noCard.text')} />
        </div>
      ) : (
        <>
          <Facts tasks={tasks.data?.overdue} open={tasks.data?.open} day={day.data} now={now} />
          <div className="layout-2">
            <div className="md-col">
              {/* What AI found in letters, calls and comments waits for a decision above the tasks (#131). */}
              {/* The next step of my deals: found by rules, the pretext written by AI (#133). */}
              <NextStepPanel />
              <TaskProposalsPanel />
              <TasksPanel
                isLoading={tasks.isLoading}
                error={tasks.error}
                onRetry={() => void tasks.refetch()}
                overdue={tasks.data?.overdue ?? []}
                open={tasks.data?.open ?? []}
                now={now}
                busy={complete.isPending}
                onComplete={(id) => complete.mutate(id)}
              />
              <DealsPanel isLoading={day.isLoading} error={day.error} onRetry={() => void day.refetch()} data={day.data} />
            </div>
            <div className="md-col">
              <MeetingsPanel
                isLoading={tasks.isLoading}
                error={tasks.error}
                onRetry={() => void tasks.refetch()}
                // Свои встречи и те, куда пригласил коллега (#112): прийти надо на обе.
                open={[...(tasks.data?.open ?? []), ...(tasks.data?.invited ?? [])]}
                now={now}
              />
              <LeadsPanel isLoading={day.isLoading} error={day.error} onRetry={() => void day.refetch()} data={day.data} now={now} />
              <InvoicesPanel isLoading={day.isLoading} error={day.error} onRetry={() => void day.refetch()} data={day.data} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ сводка

function Facts({
  tasks,
  open,
  day,
  now,
}: {
  tasks: Task[] | undefined
  open: Task[] | undefined
  day: MyDay | undefined
  now: Date
}) {
  const { t, locale } = useApp()
  if (!tasks && !day) {
    return (
      <div className="facts" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="fact">
            <span className="skel" style={{ width: '40%', height: 18 }} />
            <span className="skel" style={{ width: '70%', marginTop: 6 }} />
          </div>
        ))}
      </div>
    )
  }
  const today = (open ?? []).filter((task) => !task.overdue && task.kind !== 'meeting' && dayOffset(whenOf(task), now) === 0)
  const next = today.map(whenOf).filter((when): when is string => Boolean(when)).sort()[0]
  const invoices = day?.invoices
  return (
    <nav className="facts md-facts" aria-label={t('myday.facts')}>
      {tasks && (
        <a className="fact" href="#md-tasks">
          <span className={tasks.length > 0 ? 'v flag-danger' : 'v'}>
            {tasks.length > 0 && <Icon name="clock" />}
            {tasks.length}
          </span>
          <span className="s">{t('myday.fact.overdueTasks')}</span>
        </a>
      )}
      {open && (
        <a className="fact" href="#md-tasks">
          <span className="v">{today.length}</span>
          <span className="s">
            {next ? t('myday.fact.todayTasksNext', { time: time(next, locale) }) : t('myday.fact.todayTasks')}
          </span>
        </a>
      )}
      {day?.deals.status === 'ok' && (
        <a className="fact" href="#md-deals">
          <span className={day.deals.total > 0 ? 'v flag-warning' : 'v'}>
            {day.deals.total > 0 && <Icon name="alert" />}
            {day.deals.total}
          </span>
          <span className="s">{t('myday.fact.deals')}</span>
        </a>
      )}
      {invoices?.status === 'ok' && (
        <a className="fact" href="#md-invoices">
          <span className={invoices.overdue.length > 0 ? 'v flag-danger' : 'v'}>
            {invoices.overdue.length > 0 ? formatTotals(invoices.overdueTotals, locale) : 0}
          </span>
          <span className="s">{t('myday.fact.overdueInvoices', { count: invoices.overdue.length })}</span>
        </a>
      )}
    </nav>
  )
}

// ------------------------------------------------------------------ задачи

type Period = 'today' | 'week'

function TasksPanel({
  isLoading,
  error,
  onRetry,
  overdue,
  open,
  now,
  busy,
  onComplete,
}: {
  isLoading: boolean
  error: unknown
  onRetry: () => void
  overdue: Task[]
  open: Task[]
  now: Date
  busy: boolean
  onComplete: (id: string) => void
}) {
  const { t } = useApp()
  const [period, setPeriod] = useState<Period>('today')

  // Встречи — в своём блоке справа, просроченные — своей группой сверху: строка не
  // показывается в двух местах одной колонки.
  const upcoming = open.filter((task) => {
    if (task.overdue || task.kind === 'meeting') return false
    const offset = dayOffset(whenOf(task), now)
    if (period === 'today') return offset === 0
    return offset === null || (offset >= 0 && offset < 7)
  })
  const count = overdue.length + upcoming.length

  return (
    <section className="panel" id="md-tasks" aria-labelledby="md-h-tasks">
      <div className="panel-h">
        <span className="h2" id="md-h-tasks">{t('myday.tasks')}</span>
        {!isLoading && !error && <span className="count-pill">{count}</span>}
        <span className="spacer" />
        <div className="seg" role="group" aria-label={t('myday.period')}>
          <button type="button" aria-pressed={period === 'today'} onClick={() => setPeriod('today')}>
            {t('myday.period.today')}
          </button>
          <button type="button" aria-pressed={period === 'week'} onClick={() => setPeriod('week')}>
            {t('myday.period.week')}
          </button>
        </div>
        <Link className="btn btn-ghost btn-sm" to="/tasks">{t('myday.allTasks')}</Link>
      </div>
      {isLoading ? (
        <SkeletonRows />
      ) : error ? (
        <QueryErrorState error={error} title={t('myday.tasks.error')} onRetry={onRetry} />
      ) : count === 0 ? (
        <BlockState
          kind="empty"
          compact
          title={t(period === 'today' ? 'myday.tasks.emptyToday' : 'myday.tasks.emptyWeek')}
          description={t('myday.tasks.emptyText')}
        />
      ) : (
        <>
          {overdue.length > 0 && (
            <>
              <div className="list-sub is-danger">
                <Icon name="clock" size="xs" />
                {t('myday.tasks.overdue', { count: overdue.length })}
              </div>
              {overdue.map((task) => (
                <TaskRow key={task.id} task={task} now={now} busy={busy} onComplete={onComplete} />
              ))}
            </>
          )}
          <div className="list-sub">
            {t(period === 'today' ? 'myday.tasks.today' : 'myday.tasks.week', { count: upcoming.length })}
          </div>
          {upcoming.map((task) => (
            <TaskRow key={task.id} task={task} now={now} busy={busy} onComplete={onComplete} />
          ))}
        </>
      )}
    </section>
  )
}

const KIND_ICON: Record<string, IconName> = {
  call: 'call',
  email: 'mail',
  meeting: 'meet',
  task: 'work',
}

function TaskRow({
  task,
  now,
  busy,
  onComplete,
}: {
  task: Task
  now: Date
  busy: boolean
  onComplete: (id: string) => void
}) {
  const { t, locale } = useApp()
  const when = whenOf(task)
  return (
    <div className="list-item">
      <input
        type="checkbox"
        aria-label={t('myday.tasks.complete', { subject: task.subject })}
        checked={false}
        disabled={busy}
        onChange={() => onComplete(task.id)}
      />
      <span className="feed-ico" style={{ width: 24, height: 24 }}>
        <Icon name={KIND_ICON[task.kind] ?? 'work'} size="sm" />
      </span>
      <div className="stack" style={{ flex: 1, gap: 0, minWidth: 0 }}>
        <span className="ellipsis">{task.subject}</span>
        <EntityLink type={task.entityType} id={task.entityId} />
      </div>
      {task.overdue ? (
        // Просрочка словом, а не только цветом: цвет не переживает ни печать, ни
        // дальтонизм, а просрочка — то, ради чего экран открывают.
        <span className="flag flag-danger">
          {when ? `${shortWhen(when, now, locale)} · ` : ''}
          {t('myday.overdueBy', { days: overdueDays(when, now) })}
        </span>
      ) : (
        <span className="t-sm secondary nowrap md-when">{when ? shortWhen(when, now, locale) : t('myday.noDue')}</span>
      )}
    </div>
  )
}

const ENTITY_PATH: Record<string, string> = {
  deal: '/deals',
  lead: '/leads',
  counterparty: '/counterparties',
  contact: '/contacts',
  invoice: '/invoices',
}

/** Ссылка на запись, к которой привязана задача. Названия в ответе задач нет — только вид. */
function EntityLink({ type, id }: { type?: string | null; id?: string | null }) {
  const { t } = useApp()
  // Задача без записи (#110): ключей в ответе нет вовсе.
  if (type == null || id == null) return <span className="t-sm muted">{t('tasks.noRecord')}</span>
  const base = ENTITY_PATH[type]
  const label = t(`myday.entity.${type}`)
  if (!base) return <span className="t-sm muted">{label}</span>
  return (
    <Link className="t-sm ellipsis" to={`${base}/${id}`}>
      {label}
    </Link>
  )
}

// ------------------------------------------------------------------ сделки

interface DayBlockProps {
  isLoading: boolean
  error: unknown
  onRetry: () => void
  data: MyDay | undefined
}

function DealsPanel({ isLoading, error, onRetry, data }: DayBlockProps) {
  const { t, locale } = useApp()
  const deals = data?.deals
  return (
    <section className="panel" id="md-deals" aria-labelledby="md-h-deals">
      <div className="panel-h">
        <span className="h2" id="md-h-deals">{t('myday.deals')}</span>
        {deals?.status === 'ok' && <span className="count-pill">{deals.total}</span>}
        <span className="spacer" />
        <Link className="btn btn-ghost btn-sm" to="/deals">{t('myday.allDeals')}</Link>
      </div>
      {isLoading ? (
        <SkeletonRows />
      ) : error ? (
        <QueryErrorState error={error} title={t('myday.deals.error')} onRetry={onRetry} />
      ) : !deals || deals.status === 'forbidden' ? (
        <Forbidden />
      ) : deals.items.length === 0 ? (
        <BlockState kind="empty" compact title={t('myday.deals.empty')} description={t('myday.deals.emptyText')} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('myday.deals.deal')}</th>
                <th>{t('myday.deals.client')}</th>
                <th>{t('myday.deals.stage')}</th>
                <th className="num">{t('myday.deals.amount')}</th>
                <th>{t('myday.deals.issue')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {deals.items.map((deal) => (
                <tr key={deal.id}>
                  <td>
                    <span className="stack" style={{ gap: 0 }}>
                      <Link to={`/deals/${deal.id}`}>{deal.title}</Link>
                      <span className="mono muted t-xs">{deal.number}</span>
                    </span>
                  </td>
                  <td>
                    {deal.counterpartyId ? (
                      <Link to={`/counterparties/${deal.counterpartyId}`}>{deal.counterpartyName ?? '—'}</Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <StagePips order={deal.stageOrder} total={deal.openStages} name={deal.stageName} />
                  </td>
                  <td className="num">{formatMoney(deal.amount, deal.currency, locale)}</td>
                  <td>
                    <DealIssues deal={deal} />
                  </td>
                  <td className="num">
                    <Link className="btn btn-secondary btn-sm" to={`/deals/${deal.id}`}>
                      {t(deal.noNextStep && !deal.stuck ? 'myday.deals.assignStep' : 'myday.deals.open')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deals.total > deals.items.length && (
            <div className="panel-f secondary">
              {t('myday.deals.more', { shown: deals.items.length, total: deals.total })}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

/** «Что не так» — словами, по флагу на каждую причину: сделка бывает и застрявшей, и без шага. */
function DealIssues({ deal }: { deal: AttentionDeal }) {
  const { t } = useApp()
  const now = Date.now()
  return (
    <span className="stack" style={{ gap: 4, alignItems: 'flex-start' }}>
      {deal.stuck && (
        <span className="flag flag-warning">
          <Icon name="alert" />
          {deal.maxDaysInStage != null
            ? t('myday.deals.stuck', { days: deal.daysInStage, limit: deal.maxDaysInStage })
            : t('myday.deals.stuckNoLimit', { days: deal.daysInStage })}
        </span>
      )}
      {deal.noNextStep && (
        <span className="flag flag-warning">
          <Icon name="no-step" />
          {deal.lastTouchAt
            ? t('myday.deals.noStepTouched', { days: Math.max(0, Math.floor((now - Date.parse(deal.lastTouchAt)) / DAY)) })
            : t('myday.deals.noStep')}
        </span>
      )}
    </span>
  )
}

// ------------------------------------------------------------------ встречи

function MeetingsPanel({
  isLoading,
  error,
  onRetry,
  open,
  now,
}: {
  isLoading: boolean
  error: unknown
  onRetry: () => void
  open: Task[]
  now: Date
}) {
  const { t, locale } = useApp()
  const meetings = open
    .filter((task) => task.kind === 'meeting')
    .filter((task) => {
      const offset = dayOffset(whenOf(task), now)
      return offset !== null && offset >= 0 && offset < 7
    })
    .sort((a, b) => (whenOf(a) ?? '').localeCompare(whenOf(b) ?? ''))
  return (
    <section className="panel" aria-labelledby="md-h-meet">
      <div className="panel-h">
        <span className="h2" id="md-h-meet">{t('myday.meetings')}</span>
      </div>
      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : error ? (
        <QueryErrorState error={error} title={t('myday.meetings.error')} onRetry={onRetry} />
      ) : meetings.length === 0 ? (
        <div className="panel-b secondary">{t('myday.meetings.empty')}</div>
      ) : (
        meetings.map((meeting) => {
          const when = whenOf(meeting) as string
          return (
            <div key={meeting.id} className="list-item" style={{ alignItems: 'flex-start' }}>
              <div className="md-meet-time">
                <span className="strong">{time(when, locale)}</span>
                <span className="t-xs muted">{dayLabel(when, now, locale, t)}</span>
              </div>
              <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
                <span>{meeting.subject}</span>
                <EntityLink type={meeting.entityType} id={meeting.entityId} />
                {meeting.body && <span className="t-sm muted ellipsis">{meeting.body}</span>}
              </div>
            </div>
          )
        })
      )}
    </section>
  )
}

// ------------------------------------------------------------------ лиды

function LeadsPanel({ isLoading, error, onRetry, data, now }: DayBlockProps & { now: Date }) {
  const { t } = useApp()
  const leads = data?.leads
  return (
    <section className="panel" aria-labelledby="md-h-leads">
      <div className="panel-h">
        <span className="h2" id="md-h-leads">{t('myday.leads')}</span>
        {leads?.status === 'ok' && <span className="count-pill">{leads.total}</span>}
        <span className="spacer" />
        <Link className="btn btn-ghost btn-sm" to="/leads">{t('myday.allLeads')}</Link>
      </div>
      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : error ? (
        <QueryErrorState error={error} title={t('myday.leads.error')} onRetry={onRetry} />
      ) : !leads || leads.status === 'forbidden' ? (
        <Forbidden />
      ) : leads.items.length === 0 ? (
        <div className="panel-b secondary">{t('myday.leads.empty')}</div>
      ) : (
        leads.items.map((lead) => <LeadRow key={lead.id} lead={lead} now={now} />)
      )}
    </section>
  )
}

function LeadRow({ lead, now }: { lead: NewLead; now: Date }) {
  const { t } = useApp()
  const who = [lead.companyName, lead.contactName].filter(Boolean).join(' · ')
  return (
    <div className="list-item" style={{ alignItems: 'flex-start' }}>
      <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
        <Link to={`/leads/${lead.id}`}>{lead.title}</Link>
        {who && <span className="t-sm secondary ellipsis">{who}</span>}
        <span className="t-xs muted">
          {t(`value.sales.lead.source.${lead.source}`)} · {ago(lead.createdAt, now, t)}
        </span>
      </div>
      <Link className="btn btn-secondary btn-sm" to={`/leads/${lead.id}`}>{t('myday.leads.open')}</Link>
    </div>
  )
}

// ------------------------------------------------------------------ счета

function InvoicesPanel({ isLoading, error, onRetry, data }: DayBlockProps) {
  const { t, locale } = useApp()
  const invoices = data?.invoices
  return (
    <section className="panel" id="md-invoices" aria-labelledby="md-h-inv">
      <div className="panel-h">
        <span className="h2" id="md-h-inv">{t('myday.invoices')}</span>
        <span className="spacer" />
        <Link className="btn btn-ghost btn-sm" to="/invoices">{t('myday.allInvoices')}</Link>
      </div>
      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : error ? (
        <QueryErrorState error={error} title={t('myday.invoices.error')} onRetry={onRetry} />
      ) : !invoices || invoices.status === 'forbidden' ? (
        <Forbidden />
      ) : invoices.overdue.length + invoices.awaiting.length === 0 ? (
        <div className="panel-b secondary">{t('myday.invoices.empty')}</div>
      ) : (
        <>
          {invoices.overdue.length > 0 && (
            <>
              <div className="list-sub is-danger">
                {t('myday.invoices.overdue', { amount: formatTotals(invoices.overdueTotals, locale) })}
              </div>
              {invoices.overdue.map((invoice) => (
                <InvoiceLine key={invoice.id} invoice={invoice} />
              ))}
            </>
          )}
          {invoices.awaiting.length > 0 && (
            <>
              <div className="list-sub">{t('myday.invoices.awaiting', { count: invoices.awaiting.length })}</div>
              {invoices.awaiting.map((invoice) => (
                <InvoiceLine key={invoice.id} invoice={invoice} />
              ))}
            </>
          )}
        </>
      )}
    </section>
  )
}

function InvoiceLine({ invoice }: { invoice: InvoiceRow }) {
  const { t, locale } = useApp()
  const overdue = invoice.daysOverdue != null
  const partial = invoice.outstanding !== invoice.total
  const amount = formatMoney(invoice.outstanding, invoice.currency, locale)
  return (
    <div className="list-item">
      <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
        <span className="row" style={{ gap: 6 }}>
          <Link className="mono" to={`/invoices/${invoice.id}`}>{invoice.number}</Link>
          <span className="t-sm secondary ellipsis">{invoice.counterpartyName}</span>
        </span>
        {overdue ? (
          <span className="flag flag-danger">
            {partial ? t('myday.invoices.rest', { amount }) : amount} ·{' '}
            {t('myday.overdueBy', { days: invoice.daysOverdue ?? 0 })}
          </span>
        ) : (
          <span className="t-sm secondary">
            {partial ? t('myday.invoices.rest', { amount }) : amount}
            {invoice.dueOn ? ` · ${t('myday.invoices.dueOn', { date: formatDate(invoice.dueOn, locale) })}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ общие куски

function Forbidden() {
  const { t } = useApp()
  return <BlockState kind="forbidden" compact title={t('state.forbidden.title')} description={t('state.forbidden.text')} />
}

function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="list-item">
          <span className="skel skel-c" style={{ width: 24, height: 24 }} />
          <div className="stack" style={{ gap: 6, flex: 1 }}>
            <span className="skel" style={{ width: `${50 + ((index * 13) % 30)}%` }} />
            <span className="skel" style={{ width: '30%', height: 8 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

const DAY = 24 * 60 * 60 * 1000

/** Срок задачи: дедлайн, а у встречи без дедлайна — назначенное время. */
function whenOf(task: Task): string | null {
  return task.dueAt ?? task.scheduledAt ?? null
}

/** Через сколько календарных дней (по часам смотрящего) наступит момент; null — без срока. */
export function dayOffset(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null
  const at = new Date(iso)
  const a = new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.round((a - b) / DAY)
}

function overdueDays(iso: string | null, now: Date): number {
  const offset = dayOffset(iso, now)
  return offset === null ? 0 : Math.max(0, -offset)
}

function time(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso))
}

/** «11:00» сегодня, «08.10, 16:00» в другой день: дата нужна, только когда она не сегодня. */
function shortWhen(iso: string, now: Date, locale: Locale): string {
  if (dayOffset(iso, now) === 0) return time(iso, locale)
  const date = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(new Date(iso))
  return `${date}, ${time(iso, locale)}`
}

function dayLabel(iso: string, now: Date, locale: Locale, t: (key: string) => string): string {
  const offset = dayOffset(iso, now)
  if (offset === 0) return t('myday.today')
  if (offset === 1) return t('myday.tomorrow')
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(iso))
}

function longDate(now: Date, locale: Locale): string {
  const text = new Intl.DateTimeFormat(locale, { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }).format(now)
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function ago(
  iso: string,
  now: Date,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - Date.parse(iso)) / 60_000))
  if (minutes < 60) return t('myday.ago.minutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('myday.ago.hours', { count: hours })
  return t('myday.ago.days', { count: Math.floor(hours / 24) })
}
